import { ApiError, unwrap } from '../api/envelope';
import { config } from '../config';

/**
 * The two unauthenticated dispatch-be calls the console needs. Dispatch speaks
 * the same envelope dialect as the CRM, so `unwrap` applies — a wrong tenant or
 * bad password comes back as a status:false envelope with a user-facing
 * message.
 */

async function dispatchPost<T>(path: string, tenant: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${config.dispatchBase}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        tenant,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(30_000),
    });
  } catch {
    throw new ApiError('network', 0, 'Could not reach the sign-in server. Check your connection.');
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    // Dispatch answers an unknown tenant key with a non-envelope error — turn
    // it into the message the user can act on.
    throw new ApiError(
      'badShape',
      0,
      response.ok ? 'Unexpected response from the server.' : 'Sign-in failed — check the tenant key.',
    );
  }
  return unwrap<T>(json);
}

export async function dispatchLogin(
  tenant: string,
  email: string,
  password: string,
): Promise<string> {
  const data = await dispatchPost<{ token?: string }>('/admin/login', tenant, {
    email: email.trim().toLowerCase(),
    password,
  });
  if (!data?.token) {
    throw new ApiError('badShape', 0, 'Sign-in succeeded but no token was returned.');
  }
  return data.token;
}

export async function dispatchForgotPassword(tenant: string, email: string): Promise<void> {
  await dispatchPost<unknown>('/admin/forgotPassword', tenant, {
    email: email.trim().toLowerCase(),
  });
}
